// The wait look of the Stonefish (R2848, ui-map-interface.md §6.3): card
// 05 Square Fuse of mockup round 133. A fuse in the square of the kind
// burns down with the wait left, and in the final cue the spark runs in
// to the body.
//
// Everything here is a pure function of numbers, so the clock, the fuse
// and the cue are unit-testable without a batch. `fast/layers/torpedoes.ts`
// keeps the columns and packs the marks.
//
// The clock. `TorpedoState.loiter_pct` is the wait left in whole percent
// of `loiter_sec`, one step a 36 map seconds: 1.2 wall seconds on core,
// 0.6 on an arcade map. The layer keeps two shares of the wait per round:
// the TARGET, its best estimate of the true share, and the FUSE, the share
// it draws. Both run down at the wall rate between two wire figures. The
// fuse closes on the target and never rises, so it never burns backward.
//
// The figures ride `[presentation.torpedo_wait]`; the shape of each curve
// (the smoothstep, the cubic of the cue, the flicker) is stated here as a
// named constant (R1007).

import { TORPEDO_WAIT } from '../../gen/presentation';
import { clamp01, frac, smooth } from '../../render/recipe-kit';

export const WAIT = TORPEDO_WAIT;

/** The two angular rates of the spark's flicker, in radians a wall
 *  second: the card's `sin(T · 37) · sin(T · 23)`. */
const FLICKER_A = 37, FLICKER_B = 23;

/** The share of one wire step: the wire figure is a whole percent. */
const STEP = 0.01;

/**
 * The target share after one wire figure (`pct`, 0..100).
 *
 * `prev` is the figure of the frame before, or a negative number on the
 * first sighting, where the true share is anywhere inside the rounding
 * band and the centre is the best guess. A step down means that the true
 * share just crossed the half step above the new figure, which is the
 * best fix the wire gives. A figure that holds clamps the running target
 * into its band.
 */
export function wireTarget(prev: number, pct: number, cur: number): number {
  const mid = pct * STEP;
  if (prev < 0 || pct > prev) return clamp01(mid);
  if (pct < prev) return Math.min(1, mid + STEP / 2);
  return Math.max(Math.max(0, mid - STEP / 2), Math.min(mid + STEP / 2, cur));
}

/**
 * The target one frame on: it runs down at `rate` (shares a wall second)
 * and stops at the bottom of the band the wire figure allows.
 */
export function runTarget(target: number, pct: number, rate: number, dt: number): number {
  const floor = Math.max(0, (pct - 0.5) * STEP);
  return Math.max(floor, target - rate * dt);
}

/**
 * The drawn share one frame on (R2848). It runs down at `rate` and closes
 * on `target` with the time constant `correct_sec`: a fuse behind the
 * target burns faster, and a fuse ahead of it slows down to a stop at
 * most. It never rises, so the fuse never burns backward.
 */
export function runFuse(fuse: number, target: number, rate: number, dt: number): number {
  const k = 1 - Math.exp(-dt / WAIT.correctSec);
  let f = fuse - rate * dt;
  const err = target - f;
  if (err < 0) f += err * k;
  else f += Math.min(err * k, rate * dt);
  return clamp01(f);
}

/** The share of the fuse left before the cue, from the wait left and the
 *  whole wait in wall seconds. A wait of unknown length draws `share`. */
export function fuseShare(share: number, waitSec: number): number {
  if (!(waitSec > WAIT.cueSec)) return clamp01(share);
  return clamp01((share * waitSec - WAIT.cueSec) / (waitSec - WAIT.cueSec));
}

/** How far the final cue has run, 0..1. A wait of unknown length has no
 *  cue, because the client cannot time it. */
export function cueShare(share: number, waitSec: number): number {
  if (!(waitSec > 0)) return 0;
  return clamp01((WAIT.cueSec - share * waitSec) / WAIT.cueSec);
}

/**
 * The share the fuse draws while a Torpor's flare burns the lost length
 * (R2848): it falls from the share before the cut (`from`) to the share
 * after it over `flare_sec` of `age`.
 */
export function flareShare(share: number, from: number, age: number): number {
  if (age < 0) return share;
  return share + (from - share) * (1 - smooth(0, WAIT.flareSec, age));
}

/** The flare's strength, 1 at the cut and 0 when it has burned out. A
 *  negative age is no flare. */
export function flareOf(age: number): number {
  return age < 0 ? 0 : 1 - smooth(0, WAIT.flareSec, age);
}

/** The flicker of the spark at `t` wall seconds, `1 - share` to 1. */
export function flicker(t: number): number {
  const s = WAIT.flickerShare;
  return 1 - s + s * Math.sin(t * FLICKER_A) * Math.sin(t * FLICKER_B);
}

/** The age of ember `i` of `n` at `t` wall seconds, 0..1 through its fall. */
export function emberAge(t: number, i: number, n: number): number {
  return frac(t * WAIT.emberHz + i / n);
}

/**
 * The fuse left, as a path in the round's own frame (nose to +x), into
 * `out` as `x, y` pairs: the spark first, then each corner the fuse still
 * runs through, to the start corner. It answers the number of points, 0
 * for a fuse burned out. The fuse burns from the front corner on the
 * port side (`+h, -h`) down the bow, along the starboard side, across the
 * stern and back up the port side (the card's corner order).
 */
export function fusePath(share: number, side: number, out: Float32Array): number {
  const h = side / 2, seg = side;
  const burned = 4 * seg * (1 - clamp01(share));
  let n = 0, acc = 0;
  for (let s = 0; s < 4; s++) {
    const ax = CORNER_X[s] * h, ay = CORNER_Y[s] * h;
    const bx = CORNER_X[s + 1] * h, by = CORNER_Y[s + 1] * h;
    if (acc + seg <= burned) { acc += seg; continue; }
    if (n === 0) {
      const u = Math.max(0, (burned - acc) / seg);
      out[0] = ax + (bx - ax) * u; out[1] = ay + (by - ay) * u;
      n = 1;
    }
    out[n * 2] = bx; out[n * 2 + 1] = by;
    n++;
    acc += seg;
  }
  return n;
}

/** The five corners of the fuse in half sides, the start corner twice: the
 *  card's corner order, which the ash outline walks too. */
export const CORNER_X = [1, 1, -1, -1, 1];
export const CORNER_Y = [-1, 1, 1, -1, -1];
